import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { api } from '../services/api';
import { X, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

import { getApiBaseUrl } from '../utils/urlHelper';

const API_URL = getApiBaseUrl();
const PREVIEW_COUNT = 8;

const galleryText = {
  pt: { title: 'Galeria', subtitle: 'Momentos das nossas peregrinações', seeAll: 'Ver Todas as Fotos' },
  en: { title: 'Gallery', subtitle: 'Moments from our pilgrimages', seeAll: 'See All Photos' },
  es: { title: 'Galería', subtitle: 'Momentos de nuestras peregrinaciones', seeAll: 'Ver Todas las Fotos' },
};

const HomeGallery = () => {
  const [images, setImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const { language } = useLanguage();
  const gt = galleryText[language] || galleryText.pt;

  useEffect(() => {
    api.getGallery().then(res => setImages(res.data)).catch(() => {});
  }, []);

  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(-1);
  const goNext = useCallback(() => setLightboxIndex(i => (i + 1) % images.length), [images.length]);
  const goPrev = useCallback(() => setLightboxIndex(i => (i - 1 + images.length) % images.length), [images.length]);

  useEffect(() => {
    const handleKey = (e) => {
      if (lightboxIndex < 0) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxIndex, goNext, goPrev]);

  if (images.length === 0) return null;

  const previewImages = images.slice(0, PREVIEW_COUNT);
  const hasMore = images.length > PREVIEW_COUNT;

  return (
    <section id="galeria" className="py-16 bg-gray-50" data-testid="home-gallery">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-10" data-section-head>
          <h2 className="text-3xl md:text-4xl font-bold text-demarre-900 mb-2">{gt.title}</h2>
          <p className="text-gray-600">{gt.subtitle}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {previewImages.map((img, idx) => (
            <div
              key={img._id || idx}
              className="relative group overflow-hidden rounded-lg aspect-square cursor-pointer"
              onClick={() => openLightbox(idx)}
              data-testid={`home-gallery-thumb-${idx}`}
            >
              <img
                src={`${API_URL}${img.path || img.image_url}`}
                alt={img.filename || img.caption_pt || `Foto ${idx + 1}`}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
            </div>
          ))}
        </div>

        {hasMore && (
          <div className="text-center mt-8">
            <Link to="/galeria">
              <Button variant="outline" className="border-demarre-700 text-demarre-700 hover:bg-demarre-50 px-8" data-testid="see-all-gallery-btn">
                {gt.seeAll} ({images.length}) <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxIndex >= 0 && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
          data-testid="home-gallery-lightbox"
        >
          <button className="absolute top-4 right-4 text-white/70 hover:text-white z-50 p-2" onClick={closeLightbox} data-testid="home-lightbox-close">
            <X className="w-8 h-8" />
          </button>
          <div className="absolute top-4 left-4 text-white/70 text-sm">{lightboxIndex + 1} / {images.length}</div>
          <button className="absolute left-2 md:left-6 text-white/60 hover:text-white p-2 z-50" onClick={(e) => { e.stopPropagation(); goPrev(); }} data-testid="home-lightbox-prev">
            <ChevronLeft className="w-10 h-10" />
          </button>
          <img
            src={`${API_URL}${images[lightboxIndex]?.path || images[lightboxIndex]?.image_url}`}
            alt={images[lightboxIndex]?.filename || images[lightboxIndex]?.caption_pt || ''}
            className="max-h-[85vh] max-w-[90vw] object-contain select-none"
            onClick={(e) => e.stopPropagation()}
            data-testid="home-lightbox-image"
          />
          <button className="absolute right-2 md:right-6 text-white/60 hover:text-white p-2 z-50" onClick={(e) => { e.stopPropagation(); goNext(); }} data-testid="home-lightbox-next">
            <ChevronRight className="w-10 h-10" />
          </button>
        </div>
      )}
    </section>
  );
};

export default HomeGallery;
